import React, { useState, useEffect } from 'react';
import { Advertisement } from '../../types/advertisement';

interface ModalBackdropProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

interface AdFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  ad: Advertisement | null;
  isCreateMode: boolean;
  onSave: (ad: Partial<Advertisement>) => Promise<void>;
}

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
}

const AdManagementTab: React.FC = () => {
  const [ads, setAds] = useState<Advertisement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAd, setSelectedAd] = useState<Advertisement | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/admin/ads`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch ads');
      const data = await response.json();
      setAds(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAd = async (adData: Partial<Advertisement>) => {
    try {
      // Filter out undefined values to prevent D1_TYPE_ERROR
      const cleanedData = Object.fromEntries(
        Object.entries(adData).filter(([_, value]) => value !== undefined)
      );
      
      console.log('Saving ad with cleaned data:', cleanedData);
      
      if (isCreateMode) {
        // Create new ad
        const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/admin/ads`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(cleanedData)
        });

        if (!response.ok) throw new Error('Failed to create ad');
      } else if (selectedAd) {
        // Update existing ad
        const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/admin/ads/${selectedAd.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(cleanedData)
        });

        if (!response.ok) throw new Error('Failed to update ad');
      }

      // Refresh the ad list
      await fetchAds();
      setSelectedAd(null);
      setIsCreateMode(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDeleteAd = async () => {
    if (!selectedAd) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/admin/ads/${selectedAd.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete ad');

      // Refresh the ad list
      await fetchAds();
      setSelectedAd(null);
      setShowDeleteDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const toggleAdStatus = async (adId: string, active: boolean) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/admin/ads/${adId}/toggle`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });

      if (!response.ok) throw new Error('Failed to toggle ad status');

      // Refresh the ad list
      await fetchAds();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  // Modal backdrop component
  const ModalBackdrop: React.FC<ModalBackdropProps> = ({ children, isOpen, onClose }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div 
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />
        <div className="relative z-50">
          {children}
        </div>
      </div>
    );
  };

  // Confirmation dialog component
  const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    confirmText = "Confirm",
    cancelText = "Cancel"
  }) => {
    if (!isOpen) return null;

    return (
      <ModalBackdrop isOpen={isOpen} onClose={onClose}>
        <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4 border border-slate-700">
          <h3 className="text-lg font-semibold text-slate-100 mb-2">
            {title}
          </h3>
          <p className="text-slate-300 mb-6">
            {description}
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-slate-700 text-slate-100 hover:bg-slate-600"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </ModalBackdrop>
    );
  };

  // Ad form modal component
  const AdFormModal: React.FC<AdFormModalProps> = ({
    isOpen,
    onClose,
    ad,
    isCreateMode,
    onSave
  }) => {
    const [formData, setFormData] = useState<Partial<Advertisement>>({
      name: '',
      provider: 'google',
      content: '',
      targetUrl: '',
      active: true
    });

    useEffect(() => {
      if (ad) {
        setFormData(ad);
      } else {
        setFormData({
          name: '',
          provider: 'google',
          content: '',
          targetUrl: '',
          active: true
        });
      }
    }, [ad, isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      await onSave(formData);
      onClose();
    };

    if (!isOpen) return null;

    return (
      <ModalBackdrop isOpen={isOpen} onClose={onClose}>
        <div className="bg-slate-800 rounded-lg shadow-lg max-w-2xl w-full mx-4 border border-slate-700">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-slate-100 mb-4">
              {isCreateMode ? 'Create New Advertisement' : 'Edit Advertisement'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-2 rounded-md bg-slate-700 text-slate-100 border border-slate-600"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Provider
                </label>
                <select
                  name="provider"
                  value={formData.provider}
                  onChange={handleChange}
                  className="w-full p-2 rounded-md bg-slate-700 text-slate-100 border border-slate-600"
                >
                  <option value="google">Google AdSense</option>
                  <option value="affiliate">Affiliate/Custom</option>
                </select>
              </div>
              
              {formData.provider === 'google' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    AdSense Code
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    rows={5}
                    className="w-full p-2 rounded-md bg-slate-700 text-slate-100 border border-slate-600 font-mono text-sm"
                    placeholder="<script async src='https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-XXXXXXXX'></script>..."
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Paste the full AdSense code snippet here, including the script tags.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      HTML Content
                    </label>
                    <textarea
                      name="content"
                      value={formData.content}
                      onChange={handleChange}
                      rows={5}
                      className="w-full p-2 rounded-md bg-slate-700 text-slate-100 border border-slate-600 font-mono text-sm"
                      placeholder="<a href='https://example.com/affiliate'><img src='https://example.com/banner.jpg' alt='Affiliate Offer' /></a>"
                      required
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Paste the full HTML for your affiliate ad, including image tags and anchor links.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Target URL (for click tracking)
                    </label>
                    <input
                      type="url"
                      name="targetUrl"
                      value={formData.targetUrl}
                      onChange={handleChange}
                      className="w-full p-2 rounded-md bg-slate-700 text-slate-100 border border-slate-600"
                      placeholder="https://example.com/affiliate"
                      required
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Enter the destination URL for click tracking purposes. This should match the href in your HTML content.
                    </p>
                  </div>
                </>
              )}
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-slate-300">
                  Active
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-md bg-slate-700 text-slate-100 hover:bg-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  {isCreateMode ? 'Create' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </ModalBackdrop>
    );
  };

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-100">Ad Management</h1>
        <button
          onClick={() => {
            setSelectedAd(null);
            setIsCreateMode(true);
          }}
          className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
        >
          Create New Ad
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500 rounded-md text-red-500">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Provider</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Views</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Clicks</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">CTR</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {ads.map((ad) => (
                  <tr key={ad.id} className="hover:bg-slate-700/50">
                    <td className="px-6 py-4 text-sm text-slate-200">{ad.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{ad.provider}</td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ad.active
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}
                      >
                        {ad.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">{ad.views}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{ad.clicks}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">
                      {ad.views > 0 ? ((ad.clicks / ad.views) * 100).toFixed(2) + '%' : '0.00%'}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedAd(ad);
                            setIsCreateMode(false);
                          }}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAd(ad);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-400 hover:text-red-300"
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => toggleAdStatus(ad.id, !ad.active)}
                          className="text-amber-400 hover:text-amber-300"
                        >
                          {ad.active ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ad Edit/Create Modal */}
      <AdFormModal
        isOpen={isCreateMode || selectedAd !== null}
        onClose={() => {
          setSelectedAd(null);
          setIsCreateMode(false);
        }}
        ad={selectedAd}
        isCreateMode={isCreateMode}
        onSave={handleSaveAd}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteAd}
        title="Delete Advertisement"
        description="Are you sure you want to delete this advertisement? This action cannot be undone."
        confirmText="Delete"
      />
    </div>
  );
};

export default AdManagementTab;
